import React, { useEffect, useMemo } from "react";
import {
	Box,
	Flex,
	Grid,
	HStack,
	Modal,
	ModalBody,
	ModalContent,
	ModalOverlay,
	Text,
	useDisclosure,
} from "@chakra-ui/react";
import { SubmitHandler } from "react-hook-form";
import TakeBreakForm from "./TakeBreakForm";
import BongaTag from "../general/BongaTag";
import strings from "../../localization/main";
import { GiEmptyHourglass } from "react-icons/gi";
import { getItem, notify, postItem } from "../../gateway/Functions";
import { useDispatch, useSelector } from "react-redux";
import { setProfile, updateTables } from "../../redux/actions/actions";
import { useNavigate } from "react-router-dom";
import { Result, Spin } from "antd";
import { primaryColor } from "../../theme/theme";
import MyTable from "../MyTable";
import moment from "moment";
import { MdList, MdListAlt } from "react-icons/md";
import CustomButton from "../buttons/CustomButton";
import { userSignOut } from "../auth/FireBase";

const TakeBreak = () => {
	const { onOpen, isOpen, onClose } = useDisclosure();
	const [loading, setLoading] = React.useState(false);
	const [showBreaks, setShowBreaks] = React.useState(false);
	const [breaks, setBreaks] = React.useState<any[]>([]);
	const user = useSelector((state: any) => state.User);
	const inBreak = useSelector((state: any) => state.Profile.in_break);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const update = useSelector((state: any) => state.UpdateTables);

	useEffect(() => {
		if (inBreak) {
			onOpen();
		}
	}, [inBreak]);

	useEffect(() => {
		getItem(`users/${user?.id}/breaks`).then((res: any) => {
			setBreaks(res?.data);
		});
	}, [update]);

	const closeModal = () => {
		onClose();
		setShowBreaks(false);
	};

	const onSubmit: SubmitHandler<any> = (data: any) => {
		setLoading(true);
		data.reason = data.name;
		delete data.name;
		data.user = user?.id;
		postItem("users/take_break", data)
			.then(() => {
				getItem(`profile/${user?.id}`).then((res: any) => {
					dispatch(setProfile(res.data));
					dispatch(updateTables());
				});
				notify("success", "bottomLeft", strings.success, strings.taken_break);
			})
			.catch((err: any) => {
				notify("error", "bottomLeft", "Error", strings.failed_break);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const onResume = () => {
		setLoading(true);
		postItem("users/return_from_break", { user: user?.id })
			.then(() => {
				getItem(`profile/${user?.id}`).then((res: any) => {
					onClose();
					dispatch(setProfile(res.data));
					dispatch(updateTables());
				});
				userSignOut(dispatch, navigate);
			})
			.catch((err: any) => {
				notify(
					"error",
					"bottomLeft",
					"Error",
					strings.failed_resume_from_break
				);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const columns = useMemo(() => {
		return [
			{
				title: "Reason",
				dataIndex: "reason",
				key: "reason",
			},
			{
				title: "Start Time",
				dataIndex: "date_created",
				key: "date_created",
				render: (date_created: string) => (
					<Text>
						{moment(date_created).format("HH:mm:ss")}
						{", "}
						<Text as={"span"} fontWeight={"bold"}>
							{moment(date_created).format("DD MMM YYYY")}
						</Text>
					</Text>
				),
			},
			{
				title: "End Time",
				dataIndex: "date_resumed",
				key: "date_resumed",
				render: (date_resumed: string) =>
					date_resumed ? (
						<Text>
							{moment(date_resumed).format("HH:mm:ss")}
							{", "}
							<Text as={"span"} fontWeight={"bold"}>
								{moment(date_resumed).format("DD MMM YYYY")}
							</Text>
						</Text>
					) : (
						"N/A"
					),
			},
		];
	}, []);

	return (
		<Box>
			<Grid w={"100%"} h={"100%"}></Grid>
			{!inBreak && (
				<BongaTag onClick={onOpen}>
					<GiEmptyHourglass /> {strings.take_break}
				</BongaTag>
			)}
			<Modal
				closeOnOverlayClick={false}
				isOpen={isOpen}
				onClose={onClose}
				isCentered
				size={"3xl"}
			>
				<ModalOverlay />
				<ModalContent onClick={(e: any) => e.stopPropagation()}>
					<ModalBody>
						<Spin spinning={loading}>
							{!inBreak ? (
								<Result
									icon={
										<GiEmptyHourglass
											style={{ fontSize: "50px", color: primaryColor }}
										/>
									}
									title={strings.take_break}
									extra={
										<>
											<TakeBreakForm
												loading={loading}
												onSubmit={onSubmit}
												title={strings.take_break}
												onClose={closeModal}
											/>

											<Flex justifyContent={"flex-end"}>
												<Text
													w={"fit-content"}
													mt={"1rem"}
													p={".2rem"}
													borderRadius={"3px"}
													bg={"gray.100"}
													cursor={"pointer"}
													onClick={() => setShowBreaks(!showBreaks)}
													_hover={{
														color: primaryColor,
														backgroundColor: "gray.200",
													}}
												>
													<MdList />
													{showBreaks ? "Hide Breaks" : "Show Breaks"}
												</Text>
											</Flex>

											{showBreaks && (
												<Flex
													maxH={"20vh"}
													overflow={"scroll"}
													mt={"1rem"}
													w={"100%"}
												>
													<MyTable
														title={strings.my_breaks}
														action={<Box></Box>}
														data={breaks}
														columns={columns}
													/>
												</Flex>
											)}
										</>
									}
								/>
							) : (
								<Result
									icon={
										<GiEmptyHourglass
											style={{ fontSize: "50px", color: primaryColor }}
										/>
									}
									title={strings.currently_on_break}
									extra={
										<>
											<HStack
												gap={"10px"}
												justifyContent={"center"}
												w={"100%"}
												alignItems={"center"}
												h={"1rem"}
											>
												<CustomButton onClick={onResume} colorScheme={"blue"}>
													<GiEmptyHourglass /> {strings.resume}
												</CustomButton>
												<CustomButton
													colorScheme={"gray"}
													onClick={() => setShowBreaks(!showBreaks)}
												>
													<MdListAlt />
													{showBreaks ? "Hide Breaks" : "Show Breaks"}
												</CustomButton>
											</HStack>
											{showBreaks && (
												<Flex
													maxH={"20vh"}
													overflow={"scroll"}
													mt={"1rem"}
													w={"100%"}
												>
													<MyTable data={breaks} columns={columns} />
												</Flex>
											)}
										</>
									}
								/>
							)}
						</Spin>
					</ModalBody>
				</ModalContent>
			</Modal>
		</Box>
	);
};

export default TakeBreak;
